import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { IESDoc, FilterGroup, TableColumn } from "../../../../../app.interface";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { POSSellableItemFormDialog } from "./POSSellableItemFormDialog";
import { POSSellableItemDetailsDrawer } from "./POSSellableItemDetailsDrawer";
import {
  posSellableItemsService,
  POSSellableItem,
} from "../../../services/pos-sellable-items.service";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import dayjs from "dayjs";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

type POSSellableItemDoc = IESDoc<POSSellableItem>;

export const SaleItems: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = React.useState<POSSellableItemDoc[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] =
    React.useState<POSSellableItemDoc | null>(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);

  const filterGroups: FilterGroup[] = [
    {
      id: "category",
      label: "Category",
      type: "select",
      icon: "category",
      description: "Filter items by their category",
      options: [
        {
          id: "main_course",
          label: "Main Course",
          value: "main_course",
          icon: "restaurant",
          color: "#0891B2",
        },
        {
          id: "appetizer",
          label: "Appetizer",
          value: "appetizer",
          icon: "tapas",
          color: "#0891B2",
        },
        {
          id: "dessert",
          label: "Dessert",
          value: "dessert",
          icon: "cake",
          color: "#0891B2",
        },
        {
          id: "beverage",
          label: "Beverage",
          value: "beverage",
          icon: "local_bar",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter items by their availability",
      options: [
        {
          id: "available",
          label: "Available",
          value: "available",
          icon: "check_circle",
          color: "#059669",
        },
        {
          id: "unavailable",
          label: "Unavailable",
          value: "unavailable",
          icon: "cancel",
          color: "#DC2626",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort items by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "price_asc",
          label: "Cost (Low to High)",
          value: "total_cost_asc",
          icon: "trending_up",
        },
        {
          id: "price_desc",
          label: "Cost (High to Low)",
          value: "total_cost_desc",
          icon: "trending_down",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "name",
      header: "Item Details",
      field: "name",
      type: "action",
      width: "250px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.description}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Craeted at: {dayjs(row.created_at).format("MMM D, YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      id: "category",
      header: "Category & Cost",
      field: "category",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>restaurant_menu</Icon>
            {row.category || "Uncategorized"}
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#059669", 0.08),
              color: "#059669",
              border: "1px solid",
              borderColor: alpha("#059669", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>calculate</Icon>
            Total Cost: {row.total_cost?.toFixed(3)} BHD
          </Box>
        </Stack>
      ),
    },
    {
      id: "status",
      header: "Status",
      field: "is_available",
      type: "action",
      width: "150px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#059669", 0.08),
              color: "#059669",
              border: "1px solid",
              borderColor: alpha("#059669", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>check_circle</Icon>
            Available
          </Box>
        </Stack>
      ),
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Delete Item" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.menu_items.delete",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  React.useEffect(() => {
    loadItems();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await posSellableItemsService.listPOSSellableItems({
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        ...filters,
      });
      setItems(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      toast.error("Failed to load menu items");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = () => {
    setSelectedItem(null);
    setFormDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    try {
      setSaving(true);
      await posSellableItemsService.deletePOSSellableItem(selectedItem._id);
      toast.success("Menu item deleted successfully");
      handleCloseDeleteDialog();
      loadItems();
    } catch (error) {
      toast.error("Failed to delete menu item");
      console.error("Error deleting menu item:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (item: IESDoc) => {
    setSelectedItem(item as POSSellableItemDoc);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedItem(null);
    setDeleteDialogOpen(false);
  };

  const handleCloseFormDialog = () => {
    setSelectedItem(null);
    setFormDialogOpen(false);
    loadItems();
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleDetailsOpen = (item: IESDoc) => {
    setSelectedItem(item as POSSellableItemDoc);
    setDetailsDrawerOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedItem(null);
    setDetailsDrawerOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Sellable Items"
        subtitle="Manage your restaurant's sellable items"
        icon="ramen_dining"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleCreateItem}
            disabled={
              !hasPermission(
                "inventory.menu_items.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Sellable Item
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search sellable items by name or description..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={items}
          columns={MasterColumns}
          loading={loading}
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          pagination={{
            total: totalItems,
            page: page,
            pageSize: pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          header={{
            title: "Sellable Items List",
            subtitle: "Your current list of sellable items",
          }}
          onRowClick={(row) => handleDetailsOpen(row as POSSellableItemDoc)}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: loadItems,
            },
            export: {
              label: "Export Items",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "sellable-items-list",
              },
            },
          }}
          emptyState={{
            icon: "ramen_dining",
            title: "No Sellable Items Found",
            description: search
              ? "No sellable items match your search criteria. Try adjusting your filters or search term."
              : "Get started by adding your first sellable item.",
            actionLabel: "Add Sellable Item",
            onAction: handleCreateItem,
          }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteItem}
        title="Delete Sellable Item"
        itemName={selectedItem?.name || ""}
        itemDescription={selectedItem?.description || ""}
        loading={saving}
        icon="ramen_dining"
      />

      {/* Form Dialog */}
      {formDialogOpen && (
        <POSSellableItemFormDialog
          open={formDialogOpen}
          onClose={handleCloseFormDialog}
          onSave={loadItems}
          initialData={null}
          mode="add"
        />
      )}

      {/* Details Drawer */}
      {detailsDrawerOpen && selectedItem && (
        <POSSellableItemDetailsDrawer
          open={detailsDrawerOpen}
          onClose={handleDetailsClose}
          item={selectedItem}
        />
      )}
    </Box>
  );
};
